/**
 * PlayerScorecardModal.jsx
 * ============================================================================
 * The leaderboard drill-down card. Tap a player on any spectator board and see
 * what he actually shot, hole by hole.
 *
 * Origin: Dan Moylan (TRKMGL) — "good to see who was leading but wanted to know
 * what they did to get there." Plan: Player_Scorecard_Modal_Plan_v2_1.md.
 *
 * ONE COMPONENT, THREE CALL SITES. The board JSX is hand-triplicated across
 * LiveScoring, PublicLeaderboard and LeaderboardModal in App.js. This component
 * exists so the CARD is not triplicated too — same reason holeSetSuffix() and
 * boardSectionHeader() were extracted. Do not inline it back into any renderer.
 *
 * THE ONE RULE THAT MATTERS (plan §1.1):
 *   The LEADERBOARD stops at the round designation (9H or 18H).
 *   The CARD is always 18H, filled in with whatever the player entered.
 *
 * So on a front9 round where a player kept going, the card totals ELEVEN holes
 * while the board behind it totals NINE — and both numbers are right. They
 * answer different questions ("what did he shoot?" vs "who is winning?"). The
 * footnote at the bottom appears only in that case, to head off the "is this a
 * bug?" text message. Do NOT reconcile these two totals.
 *
 * NO HOLE-SET LOGIC LIVES HERE. The component renders 18 slots and dashes what
 * is absent. Holes outside the competition set arrive with net === null from
 * spectatorBoardService (there is no stroke allocation outside the set, and the
 * course handicap on a nine is a NINE-hole handicap), so the dash rule alone
 * produces the correct display. That is the design's main virtue — don't
 * reintroduce branching on competition_hole_set.
 *
 * PAR comes from board.hole_meta[row.course_tee_id], never from the score rows.
 * Par belongs to the TEE (Rule 19): Harbor #4 is par 4 from most tees and par 5
 * for ladies off Gold, so a single board-level par row would be wrong for half
 * a mixed field.
 *
 * STATELESS. Takes a row + the board and renders. Each caller owns its own
 * `openPlayerId`. Holes arrive as part of `player`, so if the embedded payload
 * ever has to become fetch-on-demand (plan §3.3), only the call sites change.
 *
 * LINE ENDINGS: LF, matching its sibling ScorecardAllPlayers.jsx in this folder.
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';

const DASH = '\u2014';
const FRONT = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const BACK = [10, 11, 12, 13, 14, 15, 16, 17, 18];
const ALL = [...FRONT, ...BACK];

const HOLE_SET_LABEL = { front9: 'Front 9', back9: 'Back 9', all18: null };

// Palette — the hex values already used across App.js and ScorecardAllPlayers.jsx.
// The codebase has no CSS custom properties; matching the existing convention
// beats introducing a token file only one component imports.
const C = {
  headerBand: '#f9fafb',   // course-reference band (the pre-printed part of a card)
  subtotal: '#f3f4f6',     // OUT / IN / TOT columns — one step darker than the band
  label: '#9ca3af',        // small grey uppercase row labels
  meta: '#6b7280',         // header values, Net row, sub-line
  gross: '#111827',        // the number the eye should land on
  border: '#e5e7eb',
  borderStrong: '#d1d5db', // card edge
  rule: '#374151',         // the header/scores divider — strongest line on the card
  white: '#ffffff',
};

function holesForSet(set) {
  if (set === 'front9') return FRONT;
  if (set === 'back9') return BACK;
  return ALL;
}

/**
 * Sum what's there; return null when there's nothing (plan §1.1). Applied
 * uniformly to Front, Back and Total — this single rule is what removes the
 * need for any hole-set special-casing in the card.
 */
function sumField(holes, list, field) {
  let total = 0;
  let any = false;
  for (const h of list) {
    const v = holes[h] ? holes[h][field] : null;
    if (v != null) { total += v; any = true; }
  }
  return any ? total : null;
}

function fmt(v) {
  return v == null ? DASH : v;
}

/**
 * Layout branch. The codebase styles inline (no Tailwind, no CSS media queries),
 * so the breakpoint is a JS width read — mirroring how the rest of App.js
 * branches on width.
 */
function useIsNarrow(breakpoint = 640) {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
}

// hideNet (Sept 14, 2026): a team-scored (captain's choice) card has no per-hole net
// until the game type supplies a team handicap — the Net row/column is omitted rather
// than shown as a row of dashes. Callers pass board.scoring_mode === 'team' && !board.has_net.
const PlayerScorecardModal = ({ player, board, onClose, hideNet = false }) => {
  const isNarrow = useIsNarrow();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!player) return null;

  const holes = player.holes || {};
  const teeMeta = (board && board.hole_meta && player.course_tee_id)
    ? board.hole_meta[player.course_tee_id]
    : null;
  const meta = (teeMeta && teeMeta.holes) ? teeMeta.holes : {};
  const teeName = teeMeta ? teeMeta.tee_name : null;

  const holeSet = (board && board.competition_hole_set) || 'all18';
  const setLabel = HOLE_SET_LABEL[holeSet];
  const inSet = new Set(holesForSet(holeSet));

  // The §1.1 row-3 case: holes played outside the competition set. Drives the
  // footnote, and nothing else — the dash rule handles the display on its own.
  const outOfSetScored = ALL.filter(h => !inSet.has(h) && holes[h] && holes[h].gross != null);

  const par = (h) => (meta[h] ? meta[h].par : null);
  const gross = (h) => (holes[h] ? holes[h].gross : null);
  // A picked-up hole reads "7x" (Sept 27, 2026). Display only — the totals sum the numbers.
  const grossShown = (h) => { const v = gross(h); return (v != null && holes[h] && holes[h].picked) ? `${v}x` : v; };
  const net = (h) => (holes[h] ? holes[h].net : null);

  const outGross = sumField(holes, FRONT, 'gross');
  const inGross = sumField(holes, BACK, 'gross');
  const outNet = sumField(holes, FRONT, 'net');
  const inNet = sumField(holes, BACK, 'net');
  const totGross = (outGross == null && inGross == null) ? null : (outGross || 0) + (inGross || 0);
  const totNet = (outNet == null && inNet == null) ? null : (outNet || 0) + (inNet || 0);

  const parOut = ALL.some(h => par(h) != null) ? sumParList(meta, FRONT) : null;
  const parIn = ALL.some(h => par(h) != null) ? sumParList(meta, BACK) : null;
  const parTot = (parOut == null && parIn == null) ? null : (parOut || 0) + (parIn || 0);

  const subLine = [
    !isNarrow && board ? board.course_name : null,
    teeName ? `${teeName} tee` : null,
    player.course_hcp != null ? `${player.team ? 'Team' : 'Course'} HCP ${player.course_hcp}` : null,
    // A stroke play game on the board (Sept 23, 2026): the strokes in the Net row came
    // off the game's playing handicap, so say so when it differs from the course handicap.
    player.playing_handicap != null && player.playing_handicap !== player.course_hcp
      ? (player.scratch_no_index ? 'plays as scratch (no index)' : `Playing HCP ${player.playing_handicap}`)
      : null,
    `Thru ${player.thru || 0}${setLabel ? ` (${setLabel})` : ''}`,
    // A Best Ball team row (Sept 27, 2026): the team's best-ball card, not a player's.
    player.card_note || null,
  ].filter(Boolean).join(' \u00b7 ');

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 12, zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.white, borderRadius: 12, padding: isNarrow ? '16px 14px' : '18px 20px',
          width: '100%', maxWidth: isNarrow ? 460 : 780,
          maxHeight: '90vh', overflowY: 'auto',
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        }}
      >
        {/* Title block */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 500, color: C.gross }}>{player.name}</div>
            <div style={{ fontSize: 12, color: C.meta, marginTop: 3 }}>{subLine}</div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close scorecard"
            style={{
              flexShrink: 0, width: 30, height: 30, borderRadius: 8, cursor: 'pointer',
              background: C.white, border: `1px solid ${C.borderStrong}`,
              color: C.meta, fontSize: 15, lineHeight: 1,
            }}
          >
            {'\u2715'}
          </button>
        </div>

        {isNarrow
          ? <MobileCard {...{ par, gross: grossShown, net, outGross, inGross, outNet, inNet, totGross, totNet, hideNet }} />
          : <WideCard {...{ par, gross: grossShown, net, outGross, inGross, outNet, inNet, totGross, totNet, parOut, parIn, parTot, hideNet }} />}

        {outOfSetScored.length > 0 && setLabel && (
          <div style={{ fontSize: 11, color: C.meta, marginTop: 10, lineHeight: 1.5 }}>
            Holes outside the {setLabel.toLowerCase()} don{'\u2019'}t count toward the competition, so they carry
            no net and aren{'\u2019'}t in the leaderboard total.
          </div>
        )}

        {player.net_unavailable_reason && (
          <div style={{ fontSize: 11, color: C.meta, marginTop: 10, lineHeight: 1.5 }}>
            {player.net_unavailable_reason === 'no_nine_hole_ratings'
              ? 'Net unavailable \u2014 this course has no 9-hole ratings on file.'
              : player.net_unavailable_reason === 'no_handicap'
                ? (player.team ? 'Net unavailable \u2014 this team has no team handicap (a player is missing a handicap).' : 'Net unavailable \u2014 no handicap on file for this player.')
                : 'Net unavailable \u2014 course hole data is missing.'}
          </div>
        )}
      </div>
    </div>
  );
};

function sumParList(meta, list) {
  let total = 0;
  let any = false;
  for (const h of list) {
    const v = meta[h] ? meta[h].par : null;
    if (v != null) { total += v; any = true; }
  }
  return any ? total : null;
}

/* ── Wide layout (>= 640px) — the live-scoring URL / TV / desktop case ──────
   The horizontal scorecard strip everyone recognises, mirroring the column
   shape of the Scorecard Report: Hole 1-9 · OUT · 10-18 · IN · TOT.        */
function WideCard({ par, gross, net, outGross, inGross, outNet, inNet, totGross, totNet, parOut, parIn, parTot, hideNet = false }) {
  const headCell = {
    padding: '5px 0', color: C.meta, fontSize: 11, textAlign: 'center',
  };
  const headLabel = {
    padding: '5px 6px', color: C.label, fontSize: 11, fontWeight: 500,
    textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'left',
  };
  const sub = { background: C.subtotal, borderLeft: `1px solid ${C.border}` };
  const scoreCell = (isGross, extra) => ({
    padding: '7px 0', textAlign: 'center', fontSize: 14, fontWeight: 600,
    color: isGross ? C.gross : C.meta, ...extra,
  });
  const rule = { borderTop: `2.5px solid ${C.rule}` };

  return (
    <div style={{ border: `1px solid ${C.borderStrong}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 660 }}>
          <colgroup>
            <col style={{ width: 52 }} />
            {FRONT.map(h => <col key={h} style={{ width: 28 }} />)}
            <col style={{ width: 34 }} />
            {BACK.map(h => <col key={h} style={{ width: 28 }} />)}
            <col style={{ width: 34 }} />
            <col style={{ width: 38 }} />
          </colgroup>
          <tbody>
            {/* Course-reference band: small, grey, uppercase. Deliberately quiet. */}
            <tr style={{ background: C.headerBand }}>
              <td style={headLabel}>Hole</td>
              {FRONT.map(h => <td key={h} style={headCell}>{h}</td>)}
              <td style={{ ...headCell, ...sub, ...headLabel, textAlign: 'center' }}>Out</td>
              {BACK.map(h => <td key={h} style={headCell}>{h}</td>)}
              <td style={{ ...headCell, ...sub, ...headLabel, textAlign: 'center' }}>In</td>
              <td style={{ ...headCell, ...sub, ...headLabel, textAlign: 'center' }}>Tot</td>
            </tr>
            <tr style={{ background: C.headerBand }}>
              <td style={headLabel}>Par</td>
              {FRONT.map(h => <td key={h} style={headCell}>{fmt(par(h))}</td>)}
              <td style={{ ...headCell, ...sub }}>{fmt(parOut)}</td>
              {BACK.map(h => <td key={h} style={headCell}>{fmt(par(h))}</td>)}
              <td style={{ ...headCell, ...sub }}>{fmt(parIn)}</td>
              <td style={{ ...headCell, ...sub }}>{fmt(parTot)}</td>
            </tr>
            {/* Spacer, then the bold rule: the header/scores divider. */}
            <tr style={{ background: C.headerBand }}><td colSpan={22} style={{ height: 5 }} /></tr>

            {/* Player-scores band: white, larger, heavier. The eye lands here. */}
            <tr style={{ background: C.white }}>
              <td style={{ ...headLabel, ...rule }}>Gross</td>
              {FRONT.map(h => <td key={h} style={scoreCell(true, rule)}>{fmt(gross(h))}</td>)}
              <td style={scoreCell(true, { ...sub, ...rule })}>{fmt(outGross)}</td>
              {BACK.map(h => <td key={h} style={scoreCell(true, rule)}>{fmt(gross(h))}</td>)}
              <td style={scoreCell(true, { ...sub, ...rule })}>{fmt(inGross)}</td>
              <td style={scoreCell(true, { ...sub, ...rule })}>{fmt(totGross)}</td>
            </tr>
            {!hideNet && (
            <tr style={{ background: C.white }}>
              <td style={headLabel}>Net</td>
              {FRONT.map(h => <td key={h} style={scoreCell(false)}>{fmt(net(h))}</td>)}
              <td style={scoreCell(false, sub)}>{fmt(outNet)}</td>
              {BACK.map(h => <td key={h} style={scoreCell(false)}>{fmt(net(h))}</td>)}
              <td style={scoreCell(false, sub)}>{fmt(inNet)}</td>
              <td style={scoreCell(false, sub)}>{fmt(totNet)}</td>
            </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Mobile layout (< 640px) — the leaderboard-on-a-phone case ─────────────
   Front 9 and Back 9 as two side-by-side 9-row columns. Halves the vertical
   height versus an 18-row list (which runs ~3 phone screens) and needs no
   horizontal scroll — a first-tee glance has to work one-handed.           */
function MobileCard({ par, gross, net, outGross, inGross, outNet, inNet, totGross, totNet, hideNet = false }) {
  return (
    <div style={{ border: `1px solid ${C.borderStrong}`, borderRadius: 8, padding: '12px 10px' }}>
      <div style={{ display: 'flex', gap: 10 }}>
        <Nine title="Front" holes={FRONT} subLabel="Out" subGross={outGross} subNet={outNet}
          par={par} gross={gross} net={net} hideNet={hideNet} />
        <Nine title="Back" holes={BACK} subLabel="In" subGross={inGross} subNet={inNet}
          par={par} gross={gross} net={net} hideNet={hideNet} />
      </div>

      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        borderTop: `2.5px solid ${C.rule}`, marginTop: 10, paddingTop: 10,
      }}>
        <span style={{ fontSize: 11, fontWeight: 500, color: C.label, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
          Total
        </span>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span style={{ fontSize: 11, color: C.label }}>Gross</span>
          <span style={{ fontSize: 16, fontWeight: 600, color: C.gross, minWidth: 26, textAlign: 'right' }}>{fmt(totGross)}</span>
          {!hideNet && <span style={{ fontSize: 11, color: C.label, marginLeft: 8 }}>Net</span>}
          {!hideNet && <span style={{ fontSize: 16, fontWeight: 600, color: C.meta, minWidth: 26, textAlign: 'right' }}>{fmt(totNet)}</span>}
        </span>
      </div>
    </div>
  );
}

function Nine({ title, holes, subLabel, subGross, subNet, par, gross, net, hideNet = false }) {
  const th = {
    fontSize: 10, color: C.label, fontWeight: 500, textAlign: 'center',
    padding: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.3px',
  };
  const tdMeta = { fontSize: 11, color: C.meta, textAlign: 'center', padding: '4px 0' };
  const tdGross = { fontSize: 13, fontWeight: 600, color: C.gross, textAlign: 'center', padding: '4px 0' };
  const tdNet = { fontSize: 13, fontWeight: 600, color: C.meta, textAlign: 'center', padding: '4px 0' };

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{
        fontSize: 11, fontWeight: 500, color: C.label, textTransform: 'uppercase',
        letterSpacing: '0.3px', paddingBottom: 6,
      }}>
        {title}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <tbody>
          <tr style={{ background: C.headerBand }}>
            <td style={th}>#</td><td style={th}>Par</td><td style={th}>Gr</td>{!hideNet && <td style={th}>Net</td>}
          </tr>
          {holes.map(h => (
            <tr key={h} style={{ borderTop: `1px solid ${C.border}` }}>
              <td style={tdMeta}>{h}</td>
              <td style={tdMeta}>{fmt(par(h))}</td>
              <td style={tdGross}>{fmt(gross(h))}</td>
              {!hideNet && <td style={tdNet}>{fmt(net(h))}</td>}
            </tr>
          ))}
          <tr style={{ borderTop: `1px solid ${C.borderStrong}`, background: C.subtotal }}>
            <td colSpan={2} style={{ ...tdMeta, color: C.label, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px', textAlign: 'left', paddingLeft: 4 }}>
              {subLabel}
            </td>
            <td style={tdGross}>{fmt(subGross)}</td>
            {!hideNet && <td style={tdNet}>{fmt(subNet)}</td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default PlayerScorecardModal;
